import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CircularProgress, Box } from '@mui/material';
import { UserRole } from '../../types/user';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireSecretAccess?: boolean;
}

/**
 * Protected route component that requires authentication
 * Optionally requires admin role
 * Optionally requires secret access
 */
export default function ProtectedRoute({ children, requireAdmin = false, requireSecretAccess = false }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (requireSecretAccess) {
    const hasSecretAccess = sessionStorage.getItem('admin_secret_access') === 'true';
    if (!hasSecretAccess) {
      return <Navigate to="/" replace />;
    }
  }

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to home if admin required but user is not admin
  if (requireAdmin) {
    const hasAdminRole = user?.roles?.some(role => 
      [UserRole.User, UserRole.Admin, UserRole.SuperAdmin, UserRole.Owner].includes(role as UserRole)
    );
    
    if (!hasAdminRole) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
}

